import { useState, memo } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Radio,
    RadioGroup,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import { useInsightFeedback } from '@/hooks';

import type { InsightFeedback as InsightFeedbackValue, InsightFeedbackReason } from '@/types/insights';

const REASONS: { value: InsightFeedbackReason; label: string }[] = [
    { value: 'wrong_answer', label: 'Wrong answer' },
    { value: 'incomplete', label: 'Incomplete' },
    { value: 'off_topic', label: 'Off-topic' },
    { value: 'other', label: 'Other' },
];

const DEFAULT_REASON: InsightFeedbackReason = 'wrong_answer';

// Mirrors MAX_FEEDBACK_COMMENT_LENGTH in insightFeedbackService.
const COMMENT_MAX_LENGTH = 1000;

interface InsightFeedbackProps {
    callId: number;
    questionId: number;
    /** The rating already saved, or null when this insight has not been rated. */
    feedback: InsightFeedbackValue | null;
}

/**
 * Thumbs up/down on a single insight. A dislike opens a dialog asking why, and clicking the
 * active icon again clears the rating.
 */
const InsightFeedback: React.FC<InsightFeedbackProps> = ({ callId, questionId, feedback }) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [reason, setReason] = useState<InsightFeedbackReason>(DEFAULT_REASON);
    const [comment, setComment] = useState('');
    const { mutate, isPending } = useInsightFeedback(callId);

    const isLiked = feedback?.rating === 1;
    const isDisliked = feedback?.rating === -1;
    // Only 'other' carries free text, and the server rejects it empty.
    const isCommentMissing = reason === 'other' && !comment.trim();

    const closeDialog = () => {
        setIsDialogOpen(false);
    };

    const handleLike = () => {
        mutate({ questionId, rating: isLiked ? null : 1 });
    };

    const handleDislike = () => {
        if (isDisliked) {
            mutate({ questionId, rating: null });
            return;
        }

        setReason(DEFAULT_REASON);
        setComment('');
        setIsDialogOpen(true);
    };

    const handleReasonChange = (value: string) => {
        const selected = REASONS.find((option) => option.value === value);

        if (selected) {
            setReason(selected.value);
        }
    };

    const handleSend = () => {
        mutate({ questionId, rating: -1, reason, comment: reason === 'other' ? comment.trim() : null });
        closeDialog();
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
            <Tooltip title={isLiked ? 'Remove rating' : 'Accurate'} placement="top">
                <span>
                    <IconButton
                        size="small"
                        disabled={isPending}
                        onClick={handleLike}
                        aria-label={isLiked ? 'Remove positive rating' : 'Rate insight as accurate'}
                        aria-pressed={isLiked}
                        data-testid="insight-feedback-like"
                        sx={{ p: 0.5, color: isLiked ? 'devblue.60' : 'gray.60' }}
                    >
                        {isLiked ? <ThumbUpIcon sx={{ fontSize: 16 }} /> : <ThumbUpOffAltIcon sx={{ fontSize: 16 }} />}
                    </IconButton>
                </span>
            </Tooltip>

            <Tooltip title={isDisliked ? 'Remove rating' : 'Inaccurate'} placement="top">
                <span>
                    <IconButton
                        size="small"
                        disabled={isPending}
                        onClick={handleDislike}
                        aria-label={isDisliked ? 'Remove negative rating' : 'Rate insight as inaccurate'}
                        aria-pressed={isDisliked}
                        data-testid="insight-feedback-dislike"
                        sx={{ p: 0.5, color: isDisliked ? 'devblue.60' : 'gray.60' }}
                    >
                        {isDisliked ? <ThumbDownIcon sx={{ fontSize: 16 }} /> : <ThumbDownOffAltIcon sx={{ fontSize: 16 }} />}
                    </IconButton>
                </span>
            </Tooltip>

            <Dialog
                open={isDialogOpen}
                onClose={closeDialog}
                fullWidth
                maxWidth="xs"
                aria-labelledby="rate-insight-title"
                data-testid="insight-feedback-dialog"
            >
                <DialogTitle id="rate-insight-title">Rate insight</DialogTitle>

                <DialogContent>
                    <Typography variant="subtitle1" component="p" sx={{ mb: 0.5 }}>
                        Please share what was wrong
                    </Typography>

                    <RadioGroup value={reason} onChange={(event) => handleReasonChange(event.target.value)}>
                        {REASONS.map((option) => (
                            <FormControlLabel
                                key={option.value}
                                value={option.value}
                                sx={{
                                    m: 0,
                                    height: 28,
                                }}
                                control={
                                    <Radio
                                        size="small"
                                        sx={{
                                            p: 0.25,
                                            mr: 1,
                                        }}
                                    />
                                }
                                label={<Typography variant="body2">{option.label}</Typography>}
                            />
                        ))}
                    </RadioGroup>

                    {reason === 'other' && (
                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                <Box
                                    component="span"
                                    sx={{
                                        fontSize: '0.75rem',
                                        color: comment.length >= COMMENT_MAX_LENGTH ? 'error.main' : 'text.secondary',
                                    }}
                                >
                                    {comment.length}/{COMMENT_MAX_LENGTH}
                                </Box>
                            </Box>
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                size="small"
                                value={comment}
                                onChange={(event) => setComment(event.target.value)}
                                placeholder="Tell us what was not working well…"
                                slotProps={{
                                    htmlInput: {
                                        maxLength: COMMENT_MAX_LENGTH,
                                        'aria-label': 'What was not working well',
                                    },
                                }}
                            />
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 2, pt: 1, pb: 1 }}>
                    <Button variant="contained" onClick={handleSend} disabled={isPending || isCommentMissing}>
                        Send
                    </Button>

                    <Button onClick={closeDialog}>Cancel</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default memo(InsightFeedback);
